import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import Banner from "./Banner/banner";
import KeyFacts from "./Key Facts/keyFacts";
import Market from "./Market Performance/market";
import service from "../../../services/service";
import { Reit, YearData, AboutDetails } from "../../../types/reit.types";
import { formatValue, formatListingDate } from "../../../utils/financialHelper";
import Financial from "./Key Financial Performance/financial";
import { useParameters } from "../../../context/parametersContext";
import { processReitData } from "../../../utils/reitBannerHelper";
import Metrics from "./Operational Metrics/metrics";
import Pattern from "./Unitholding Pattern/pattern";
import Debt from "./Debt Profile/debt";
import Maturity from "./Debt Maturity Profile/maturity";
import Performance from "./Performance Benchmark/performance";
import Summary from "./Portfolio Summary/summary";
import Portfolio from "./Portfolio Details/portfolio";
import Occupancy from "./Occupancy/occupancy";
import MoreReits from "./More REITs/moreReits";
import "./reitsDetail.less";

const ReitsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reitImages: Record<string, string> = {
    Embassy: new URL("../../../assets/Embassy_1.png", import.meta.url).href,
    Mindspace: new URL("../../../assets/Mindspace_4.png", import.meta.url).href,
    Brookfield: new URL("../../../assets/Brookfield_2.png", import.meta.url)
      .href,
    Nexus: new URL("../../../assets/Nexus_2.png", import.meta.url).href,
  };

  const { currentReit, setCurrentReit } = useParameters();

  const [keyFacts, setKeyFacts] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [productSummary, setProductSummary] = useState<string>("");
  const [sponsor, setSponsor] = useState<string>("");

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [activeSection, setActiveSection] = useState<string>("Overview");

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navItems = [
    "Overview",
    "Market Performance",
    "Key Financial Performance",
    "Operational Metrics",
    "Unitholding Pattern",
    "Debt Maturity Profile",
    "Debt Profile",
    "Performance Benchmark",
    "Portfolio Summary",
    "Portfolio Details",
    "Occupancy",
  ];

  useEffect(() => {
    if (!id) return;

    const fetchReit = async () => {
      const data: Reit | null = await service.getReitById(id);
      if (!data) return;

      setCurrentReit(data);

      if (data.years?.length) {
        const latestYearData: any = data.years[0];
        let validQuarter = null;

        if (latestYearData?.quarters?.length) {
          validQuarter = [...latestYearData.quarters]
            .reverse()
            .find((quarter: any) => {
              const hasFinancialData = quarter.financialMetrics
                ? Object.values(quarter.financialMetrics).some(
                    (metric: any) =>
                      metric?.value !== null &&
                      metric?.value !== undefined &&
                      metric?.value !== "" &&
                      !(
                        typeof metric?.value === "number" &&
                        isNaN(metric?.value)
                      )
                  )
                : false;

              const hasPortfolioData = quarter.portfolioSummary
                ? Object.values(quarter.portfolioSummary).some(
                    (metric: any) =>
                      metric?.value !== null &&
                      metric?.value !== undefined &&
                      metric?.value !== "" &&
                      !(
                        typeof metric?.value === "number" &&
                        isNaN(metric?.value)
                      )
                  )
                : false;

              return hasFinancialData || hasPortfolioData;
            });
        }

        const yearOrQuarterData = validQuarter || latestYearData;

        setKeyFacts([
          {
            label: "Total Portfolios",
            value: formatValue(
              yearOrQuarterData?.portfolioSummary?.portfolioSize?.value
            ),
          },
          {
            label: "Market Capitalisation",
            value: formatValue(
              latestYearData?.marketPositioning?.[0]?.marketCapitalization
                ?.value
            ),
          },
          {
            label: "Complete area",
            value: formatValue(
              yearOrQuarterData?.portfolioSummary?.completedArea?.value
            ),
          },
          {
            label: "Gross asset value",
            value: formatValue(yearOrQuarterData?.financialMetrics?.gAV?.value),
          },
        ]);

        const summary =
          data.about
            ?.map((a: AboutDetails) => String(a.description?.value ?? ""))
            .join("\n") || "";
        setProductSummary(summary);

        const aboutInfo = data.about?.[0];
        if (aboutInfo?.sponsors && Array.isArray(aboutInfo.sponsors)) {
          const sponsorNames = aboutInfo.sponsors.map((s) => s.name).join(", ");
          setSponsor(sponsorNames);
        } else {
          setSponsor("N/A");
        }
      }
    };

    fetchReit();
  }, [id, setCurrentReit]);

  const reitData = useMemo(() => {
    if (!currentReit) return null;

    const processed = processReitData(currentReit);
    const truncatedAssetClass = processed.assetClass || "N/A";

    const firstWord = currentReit.name.split(" ")[0];
    const image = reitImages[firstWord];

    return {
      id: currentReit._id,
      image,
      name: currentReit.name,
      locations: processed.uniqueLocations.join(", ") || "N/A",
      assetClass: truncatedAssetClass || "N/A",
      listingDate: currentReit.about?.[0]?.registeredSebiDate?.value
        ? formatListingDate(
            String(currentReit.about[0].registeredSebiDate.value)
          )
        : "N/A",

      bse: processed.bseData
        ? {
            label: "BSE",
            value: processed.bseData.closingPrice.value,
            change: Number(processed.bseData.priceChange.value) || 0,
          }
        : { label: "BSE", value: 0, change: 0 },
      nse: processed.nseData
        ? {
            label: "NSE",
            value: processed.nseData.closingPrice.value,
            change: Number(processed.nseData.priceChange.value) || 0,
          }
        : { label: "NSE", value: 0, change: 0 },
      buttons: [],
    };
  }, [currentReit]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionName = entry.target.getAttribute("data-section");
            if (sectionName) setActiveSection(sectionName);
          }
        });
      },
      { rootMargin: "-80px 0px 0px 0px", threshold: 0.25 }
    );

    Object.values(sectionRefs.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleNavItems = isMobile ? navItems : navItems.slice(0, 8);
  const hiddenNavItems = isMobile ? [] : navItems.slice(8);

  return (
    <div className="reit-details">
      {reitData && <Banner reitData={reitData} />}

      <nav className="reit-details__nav">
        <ul>
          {visibleNavItems.map((item, index) => (
            <li
              key={index}
              className={activeSection === item ? "active" : ""}
              onClick={() => {
                const section = sectionRefs.current[item];
                section?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {item}
            </li>
          ))}

          {hiddenNavItems.length > 0 && (
            <li className="nav-dropdown">
              <span
                className="nav-icon"
                onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                  e.stopPropagation();
                  setDropdownOpen((prev) => {
                    const next = !prev;

                    if (next) {
                      const handleClickOutside = (event: MouseEvent) => {
                        const nav =
                          document.querySelector(".reit-details__nav");
                        if (nav && !nav.contains(event.target as Node)) {
                          setDropdownOpen(false);
                          document.removeEventListener(
                            "mousedown",
                            handleClickOutside
                          );
                        }
                      };

                      document.addEventListener(
                        "mousedown",
                        handleClickOutside
                      );
                    }

                    return next;
                  });
                }}
              >
                {dropdownOpen ? (
                  // Chevron Up
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                ) : (
                  // Chevron Down
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </span>

              {dropdownOpen && (
                <ul className="nav-dropdown__menu">
                  {hiddenNavItems.map((item, index) => (
                    <li
                      key={index}
                      className={activeSection === item ? "active" : ""}
                      onClick={() => {
                        const section = sectionRefs.current[item];
                        section?.scrollIntoView({ behavior: "smooth" });
                        setDropdownOpen(false);
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>

      {/* Sections with refs */}
      <div
        ref={(el): void => {
          sectionRefs.current["Overview"] = el;
        }}
        data-section="Overview"
        style={{ scrollMarginTop: "80px" }}
      >
        <KeyFacts facts={keyFacts} summary={productSummary} sponsor={sponsor} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Market Performance"] = el;
        }}
        data-section="Market Performance"
        style={{ scrollMarginTop: "140px" }}
      >
        <Market reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Key Financial Performance"] = el;
        }}
        data-section="Key Financial Performance"
        style={{ scrollMarginTop: "140px" }}
      >
        <Financial reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Operational Metrics"] = el;
        }}
        data-section="Operational Metrics"
        style={{ scrollMarginTop: "140px" }}
      >
        <Metrics reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Unitholding Pattern"] = el;
        }}
        data-section="Unitholding Pattern"
        style={{ scrollMarginTop: "140px" }}
      >
        <Pattern reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Debt Maturity Profile"] = el;
        }}
        data-section="Debt Maturity Profile"
        style={{ scrollMarginTop: "140px" }}
      >
        <Maturity reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Debt Profile"] = el;
        }}
        data-section="Debt Profile"
        style={{ scrollMarginTop: "140px" }}
      >
        <Debt reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Performance Benchmark"] = el;
        }}
        data-section="Performance Benchmark"
        style={{ scrollMarginTop: "140px" }}
      >
        <Performance reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Portfolio Summary"] = el;
        }}
        data-section="Portfolio Summary"
        style={{ scrollMarginTop: "140px" }}
      >
        <Summary reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Portfolio Details"] = el;
        }}
        data-section="Portfolio Details"
        style={{ scrollMarginTop: "140px" }}
      >
        <Portfolio reit={currentReit} />
      </div>

      <div
        ref={(el): void => {
          sectionRefs.current["Occupancy"] = el;
        }}
        data-section="Occupancy"
        style={{ scrollMarginTop: "140px" }}
      >
        <Occupancy reit={currentReit} />
      </div>
      <MoreReits currentReitId={id!} />
    </div>
  );
};

export default ReitsDetail;
